/**
 * MerakiUpdateDevicesPage — bulk-edit "spreadsheet" for devices.
 *
 * Replaces the legacy Handsontable view with a much lighter inline-edit
 * table. Edits are staged client-side; Apply pushes the diff via the store.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RotateCcw, Save, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import {
  TableShell,
  TableHeadRow,
  TableRow,
  TableHeadCell,
  TableCell,
} from "@/components/ui/table-shell";
import { TableSkeleton } from "@/components/ui/skeleton";
import { NetworkPicker } from "@/components/app/network-picker";
import { MerakiSubNav } from "@/components/app/meraki-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { useMerakiDevices } from "@/stores/meraki-devices";
import type { DeviceRow, MerakiNetwork } from "@/api/schemas/meraki";
import { ApiError } from "@/api/client";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

const COLS = "1fr 0.7fr 1.1fr 1.4fr 1.6fr 0.6fr";

/** Shorter representation of a device's status, color-coded. */
function statusBadge(status: string | null | undefined) {
  if (status === "online")
    return (
      <Badge variant="ok" dot>
        online
      </Badge>
    );
  if (status === "alerting")
    return (
      <Badge variant="warn" dot>
        alerting
      </Badge>
    );
  if (status === "offline")
    return (
      <Badge variant="err" dot>
        offline
      </Badge>
    );
  return (
    <Badge variant="neutral" size="sm">
      {status ?? "—"}
    </Badge>
  );
}

export function MerakiUpdateDevicesPage() {
  const animatedRootRef = useUiAutoAnimate();

  const { data, pending, status, error, loadFor, stage, reset, apply } =
    useMerakiDevices();
  const [network, setNetwork] = useState<MerakiNetwork | null>(null);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);


  // Show staged values overlaid on top of the loaded data.
  const merged = useMemo<DeviceRow[]>(() => {
    return data.map((row) => {
      const edit = pending[row.serial];
      return edit ? { ...row, ...edit } : row;
    });
  }, [data, pending]);

  // Search filter — matches name, serial, model, mac.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return merged;
    return merged.filter(
      (r) =>
        (r.name ?? "").toLowerCase().includes(q) ||
        r.serial.toLowerCase().includes(q) ||
        (r.model ?? "").toLowerCase().includes(q) ||
        (r.mac ?? "").toLowerCase().includes(q),
    );
  }, [merged, query]);

  const stagedCount = Object.keys(pending).length;

  async function onApply() {
    if (!network) return;
    setSaving(true);
    try {
      await apply(network.id);
      toast.success(
        `Applied ${stagedCount} change${stagedCount === 1 ? "" : "s"}`,
      );
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      toast.error("Apply failed", { description: msg });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/meraki/provisioning" className="text-fg-dim hover:text-fg">
          Meraki
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Update devices</span>
      </div>

      <MerakiSubNav />

      <NoVendorKeysBanner vendor="meraki" />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Update devices
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Rename, retag, and annotate devices in bulk. Edits are staged
            locally — review the diff badge in the action bar before clicking
            Apply.
          </p>
        </div>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      {/* Picker + filter + apply bar */}
      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[2fr_1fr_auto_auto]">
            <NetworkPicker value={network} onChange={(n) => { setNetwork(n); reset(); if (n) void loadFor(n.id); }} />
            <div className="relative">
              <Search
                size={14}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
              />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter by name, serial, model, MAC…"
                className="pl-9"
                disabled={!network}
              />
            </div>
            <Button
              variant="ghost"
              onClick={() => reset()}
              disabled={stagedCount === 0}
            >
              <RotateCcw size={14} />
              Discard
            </Button>
            <Button
              onClick={() => void onApply()}
              disabled={stagedCount === 0 || saving}
            >
              <Save size={14} />
              {saving
                ? "Applying…"
                : `Apply ${stagedCount > 0 ? `(${stagedCount})` : ""}`}
            </Button>
          </div>
          {stagedCount > 0 ? (
            <div className="text-xs text-fg-mute">
              <Badge variant="info" size="sm">
                {stagedCount}
              </Badge>{" "}
              staged change{stagedCount === 1 ? "" : "s"} — press Apply to push
              all at once.
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Table */}
      {!network ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <CardTitle>Pick a network to start editing</CardTitle>
            <CardSub>The device list loads as soon as you choose one.</CardSub>
          </CardContent>
        </Card>
      ) : status === "loading" && data.length === 0 ? (
        <TableSkeleton columns={COLS} columnCount={6} rows={6} />
      ) : (
        <TableShell>
          <TableHeadRow columns={COLS}>
            <TableHeadCell>Name</TableHeadCell>
            <TableHeadCell>Status</TableHeadCell>
            <TableHeadCell>Serial · model</TableHeadCell>
            <TableHeadCell>Tags</TableHeadCell>
            <TableHeadCell>Notes</TableHeadCell>
            <TableHeadCell />
          </TableHeadRow>
          {visible.map((row) => {
            const isDirty = !!pending[row.serial];
            return (
              <TableRow
                key={row.serial}
                columns={COLS}
                className={isDirty ? "bg-aurora-soft/30" : ""}
              >
                <TableCell>
                  <Input
                    value={row.name ?? ""}
                    onChange={(e) =>
                      stage(row.serial, { name: e.target.value })
                    }
                    placeholder="(unnamed)"
                    className="h-8 px-2 text-xs"
                  />
                </TableCell>
                <TableCell>{statusBadge(row.status)}</TableCell>
                <TableCell>
                  <div className="font-mono text-[11px] text-fg">
                    {row.serial}
                  </div>
                  <div className="font-mono text-[10.5px] text-fg-mute">
                    {row.model ?? "—"}
                  </div>
                </TableCell>
                <TableCell>
                  <Input
                    value={Array.isArray(row.tags) ? row.tags.join(", ") : ""}
                    onChange={(e) =>
                      stage(row.serial, {
                        tags: e.target.value
                          .split(",")
                          .flatMap((t) => { const v = t.trim(); return v ? [v] : []; }),
                      })
                    }
                    placeholder="tag1, tag2"
                    className="h-8 px-2 text-xs"
                  />
                  {Array.isArray(row.tags) && row.tags.length > 0 ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {row.tags.map((t) => (
                        <Badge key={t} variant="neutral" size="sm">
                          {t}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Input
                    value={row.notes ?? ""}
                    onChange={(e) =>
                      stage(row.serial, { notes: e.target.value })
                    }
                    placeholder="(empty)"
                    className="h-8 px-2 text-xs"
                  />
                </TableCell>
                <TableCell className="flex justify-end">
                  {isDirty ? (
                    <Badge variant="info" size="sm">
                      edited
                    </Badge>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
          {visible.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-fg-mute">
              {query
                ? `No devices match "${query}".`
                : "No devices in this network."}
            </div>
          ) : null}
        </TableShell>
      )}
    </div>
  );
}
